import type { MemoryToolConfig } from '@timelyapp/integration-cloud-api-spec';

export const getFormattedDomain = (url?: string | null) => {
  if (!url?.startsWith('http')) return null;

  try {
    const parsedUrl = new URL(url);
    let domain = parsedUrl.hostname;
    if (domain.startsWith('www.')) {
      domain = domain.substring(4);
    }
    return domain.toLowerCase();
  } catch {
    return null;
  }
};

export const KNOWN_BROWSER_APPS = [
  'Google Chrome',
  'Safari',
  'Opera',
  'Opera GX',
  'Vivaldi',
  'Brave Browser',
  'Brave',
  'Firefox',
  'Mozilla Firefox',
  'Firefox Developer Edition',
  'Microsoft Edge',
  'Arc',
  'Chromium',
  'Tor Browser',
  'Yandex Browser',
  'Pale Moon',
  'Waterfox',
];

/**
 * Attempts to extract the tool_raw name from the window title.
 * If the "tool_raw" is being viewed in a browser, the "webapp" becomes the tool_raw, and browser becomes the window_tool_raw.
 * If the tool_raw has configuration, we'll use that title.
 */
export const extractToolNames = ({
  title,
  app,
  url,
  toolConfig,
}: {
  title: string;
  app?: string;
  url?: string | null;
  toolConfig?: MemoryToolConfig;
}): { tool_raw?: string; window_tool_raw?: string; tool_id?: string } => {
  const viaBrowser = app && KNOWN_BROWSER_APPS.includes(app);
  const appIsBrowser = toolConfig?.tool?.category === 'browser' || (!toolConfig && viaBrowser);

  // If we have a toolConfig, prioritize its title, but preserve the original app name if it's an alias
  if (toolConfig?.tool?.title && !appIsBrowser) {
    // If app is provided and it's an alias for the tool, preserve it
    const isAlias = app && toolConfig.tool.aliases?.includes(app);
    return {
      tool_raw: isAlias ? app : toolConfig.tool.title,
      window_tool_raw: viaBrowser ? app : undefined,
      tool_id: toolConfig.tool.id,
    };
  }

  // If Memory comes from a browser, and we know which tool is being used
  if (viaBrowser && !appIsBrowser) {
    return { tool_raw: app, window_tool_raw: app };
  }

  // If Memory comes from a browser, but we don't have a `toolConfig` for the actual tool,
  // fallback to the domain (unless localhost), or last segment of the title.
  if (viaBrowser && appIsBrowser) {
    const segments = title.split(/ - | – | • /);
    const fallbackProbableTool = segments[segments.length - 1];
    const domain = getFormattedDomain(url);
    const bestGuess = domain === 'localhost' ? fallbackProbableTool : domain;
    return { tool_raw: bestGuess || fallbackProbableTool, window_tool_raw: app };
  }

  return { tool_raw: app };
};
